import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { easings, useTransition } from '@react-spring/web';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { MobileTray } from '../mobile-tray';
import { useDateTimePicker } from './utils/use-date-time-picker';
import { DateTray } from './date-tray';
import { EndTimeTray } from './end-time-tray';
import { MainTray } from './main-tray';
import { StartTimeTray } from './start-time-tray';
import ArrowForwardDisabled from '../../icons/ArrowForwardDisabled';

const MobileDateTimePicker = (props) => {
  const { intl, label } = props;

  const [showMobileTray, setShowMobileTray] = useState(false);
  const [activeTray, setActiveTray] = useState(!!dateValue ? 'main' : 'date');

  const placeholder = intl.formatMessage({
    id: 'shared-components.mobile-datetime-picker.select-date',
    defaultMessage: 'Select date'
  });

  const {
    handleContainerFocus,
    isFocused,
    setIsFocused,
    formActive,
    dateTimeDisplay,
    dateValue,
    setDateValue,
    timeValue,
    setTimeValue,
    isMounted,
    handleReset,
    blur,
    getMonthAvailability,
    monthAvailability,
    monthAvailabilityLoading,
    getDayAvailability,
    timeAvailabilityLoading,
    endTimeAvailability,
    startTimeAvailability
  } = useDateTimePicker(props);

  useEffect(() => {
    if (!showMobileTray) {
      blur();
    }
  }, [showMobileTray]);

  const getInputColorClass = () => {
    let colorClass = '';
    if (formActive && !isFocused) {
      colorClass = 'text-gray-500';
    } else {
      colorClass = 'text-gray-600';
    }
    return colorClass;
  };

  useEffect(() => {
    if (!isMounted.current || showMobileTray) {
      return;
    }
    setIsFocused(false);
  }, [showMobileTray]);

  const trayTransitions = useTransition(activeTray, {
    initial: {
      x: '0',
      opacity: 1
    },
    from: (tray) => {
      return {
        opacity: 0,
        x: tray === 'main' ? '-100%' : '100%'
      };
    },
    enter: {
      x: '0',
      opacity: 1
    },
    leave: (tray) => ({
      opacity: 0,
      x: 'main' === tray ? '-100%' : '100%'
    }),
    config: {
      duration: 600,
      easing: easings.easeOutCirc
    }
  });

  const hasValue = useMemo(() => {
    return !!dateTimeDisplay;
  }, [dateTimeDisplay]);

  const handleOpenTray = useCallback((e) => {
    e.preventDefault();
    setShowMobileTray(true);
  }, [showMobileTray, setShowMobileTray]);

  return (
    <div className="mobile-date-time-picker">
      <div
        className="datetime-input-wrap flex column"
        role='button'
        tabIndex='0'
        onFocus={(e) => {
          setShowMobileTray(true);
          handleContainerFocus(e);
        }}
      >
        <>
          {
            !!label
            && <label className="datetime-input-label text-gray-900 text-body-small-heavy text-small heavy">
              {label}
            </label>
          }
          <div className="flex middle space-between relative">
            <button
              className={classNames(
                'readonly-datetime-input btn-clear',
                'text-body-large text-base block',
                getInputColorClass(),
                { placeholder: !hasValue }
              )}
              onClick={handleOpenTray}
            >
              {dateTimeDisplay || placeholder}
            </button>

            <div className="control-button">
              {
                hasValue
                  ? <button
                    className="text-small btn-link remove-click-styling pull-right no-gutter btn-link-secondary"
                    onClick={handleOpenTray}
                    type="button"
                  >
                    <FormattedMessage
                      id="shared-components.mobile-datetime-picker.Edit"
                      defaultMessage="Edit"
                    />
                  </button>
                  : <ArrowForwardDisabled className="h4" />
              }
            </div>
          </div>
        </>
      </div>
      <MobileTray isVisible={showMobileTray} setIsVisible={setShowMobileTray}>
        {trayTransitions((transitionStyles, tray) => {
          if (tray === 'main') {
            return (
              <MainTray
                transitionStyles={transitionStyles}
                setActiveTray={setActiveTray}
                dateTimeDisplay={dateTimeDisplay}
                onReset={handleReset}
                {...props}
              />
            );
          }
          if (tray === 'date') {
            return (
              <DateTray
                transitionStyles={transitionStyles}
                setActiveTray={setActiveTray}
                setDateValue={setDateValue}
                dateValue={dateValue}
                getMonthAvailability={getMonthAvailability}
                monthAvailability={monthAvailability}
                monthAvailabilityLoading={monthAvailabilityLoading}
              />
            );
          }
          if (tray === 'startTime') {
            return (
              <StartTimeTray
                transitionStyles={transitionStyles}
                setActiveTray={setActiveTray}
                setTimeValue={setTimeValue}
                timeValue={timeValue}
                dateValue={dateValue}
                getDayAvailability={getDayAvailability}
                timeAvailabilityLoading={timeAvailabilityLoading}
                startTimeAvailability={startTimeAvailability}
              />
            );
          }
          if (tray === 'endTime') {
            return (
              <EndTimeTray
                transitionStyles={transitionStyles}
                setActiveTray={setActiveTray}
                setTimeValue={setTimeValue}
                timeValue={timeValue}
                dateValue={dateValue}
                getDayAvailability={getDayAvailability}
                timeAvailabilityLoading={timeAvailabilityLoading}
                endTimeAvailability={endTimeAvailability}
              />
            );
          }
        })}
      </MobileTray>
    </div>
  );
};

MobileDateTimePicker.propTypes = {
  formActive: PropTypes.bool,
  value: PropTypes.exact({
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date)
  }),
  setValue: PropTypes.func,
  dateValue: PropTypes.instanceOf(Date),
  setDateValue: PropTypes.func,
  timeValue: PropTypes.exact({
    startTime: PropTypes.exact({
      label: PropTypes.string,
      value: PropTypes.exact({
        h: PropTypes.number,
        m: PropTypes.number,
        overnight: PropTypes.bool
      })
    }),
    endTime: PropTypes.exact({
      label: PropTypes.string,
      value: PropTypes.exact({
        h: PropTypes.number,
        m: PropTypes.number,
        overnight: PropTypes.bool
      })
    })
  }),
  setTimeValue: PropTypes.func,
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node
  ]),
  intl: intlShape
};

exports.MobileDateTimePicker = injectIntl(MobileDateTimePicker);
